import _ from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import RoleRequire from "@/helpers/auth/RoleRequire";
import { AdminDevicesTable } from "@/types/formDataTypes";
import { Button } from "@/ui/Button";
import { Input } from "@/ui/Input/Input";
import { ColumnDef } from "@tanstack/react-table";
import { MenuDataTable } from "@/ui/DataTable/MenuDataTable";
import { Device, DeviceModel } from "@/types/types";
import { useFetch } from "@/hooks/useFetch";
import { DateTime } from "luxon";
import { TbUnlink, TbWifi } from "react-icons/tb";

export default function DevicesGrid({
  data,
  setDevices,
  onClose,
}: {
  data: any;
  setDevices: Function;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  const [currentItemId, setCurrentItemId] = useState("");

  const handleSearch = (event: any) => {
    setSearch(event.target.value);
  };

  // Фильтрация через поиск
  data = {
    nodes: data.nodes?.filter((item:any) =>
      search
        ? item.serial_number?.toLowerCase().includes(search.toLowerCase())
        : true,
    ),
  };

  const [
    { response: unlinkResponse, isLoading: unlinkIsLoading },
    doFetchUnlink,
  ] = useFetch(`/administration/devices/devices/${currentItemId}/unlink`, true);

  const [
    { response: receivedResponse, isLoading: receiveIsLoading },
    doFetchReceive,
  ] = useFetch(`/administration/devices/devices/${currentItemId}`, true);

  function onItemDisabled(e: any, id: string, receive_data: boolean): void {
    e.preventDefault();
    setCurrentItemId(id);
    doFetchReceive({
      method: "PUT",
      data: {
        data: {
          receive_data: !receive_data,
        },
      },
    });
  }

  function onItemUnlinked(e: any, id: string): void {
    e.preventDefault();
    setCurrentItemId(id);
    doFetchUnlink({
      method: "DELETE",
    });
  }

  useEffect(() => {
    if (!unlinkResponse) return;
    const originalDeviceData = data.nodes?.find(
      (device: any) => device.id == currentItemId,
    )!;
    const updatedDeviceData = {
      ...originalDeviceData,
      attributes: {
        ...originalDeviceData?.attributes,
        initialized_at: null,
        site_id: null,
        site_name: null,
      },
    };
    const withoutDevice = data.nodes?.filter(
      (device: any) => device.id != updatedDeviceData.id,
    );
    setDevices(
      [...withoutDevice!, updatedDeviceData].sort(
        (a, b) => Number(a.id) - Number(b.id),
      ),
    );
  }, [unlinkResponse]);

  useEffect(() => {
    if (!receivedResponse) return;
    const originalDeviceData = data.nodes?.find(
      (device: any) => device.id == currentItemId,
    )!;
    const updatedDeviceData = {
      ...originalDeviceData,
      attributes: {
        ...originalDeviceData?.attributes,
        receive_data: !originalDeviceData.attributes.receive_data,
      },
    };
    const withoutDevice = data.nodes?.filter(
      (device:any) => device.id != String(updatedDeviceData.id),
    )!;
    setDevices(
      [...withoutDevice, updatedDeviceData].sort(
        (a, b) => Number(a.id) - Number(b.id),
      ),
    );
  }, [receivedResponse]);

  const columns: ColumnDef<AdminDevicesTable>[] = [
    {
      header: `${t("serial_number")} / ${t("model")}`,
      size: 300,
      minSize: 300,
      maxSize: 300,
      cell: ({ row }) => (
        <div>
          <div>{row.original.serial_number}</div>
          <div>{row.original.model}</div>
        </div>
      ),
    },
    {
      header: t("name"),
      size: 200,
      minSize: 200,
      maxSize: 200,
      cell: ({ row }) => <div>{row.original.name}</div>,
    },
    {
      header: t("data_types"),
      size: 1000,
      cell: ({ row }) =>
        row.original.sensor_types
          ? row.original.sensor_types.map((item) => t(item)).join(", ")
          : "",
    },
    {
      header: t("company"),
      size: 200,
      minSize: 200,
      maxSize: 200,
      cell: ({ row }) =>
        row.original.site_id ? (
          <div>
            <div>{row.original.site_name}</div>
            <div>
              {row.original.initialized_at?.toLocaleString(DateTime.DATE_SHORT)}
            </div>
          </div>
        ) : (
          ""
        ),
    },
    {
      header: t("status"),
      size: 100,
      minSize: 100,
      maxSize: 100,
      cell: ({ row }) =>
        row.original.online ? <div>online</div> : <div>offline</div>,
    },
    {
      header: t("receive_data"),
      size: 150,
      minSize: 150,
      maxSize: 150,
      cell: ({ row }) =>
        row.original.receive_data ? <div>receive</div> : <div>not receive</div>,
    },
    {
      accessorKey: "id",
      size: 100,
      minSize: 100,
      maxSize: 100,
      header: "",
      cell: (props) => (
        <div className="flex items-center gap-1 justify-end">
          {props.row.original.site_id && (
            <Button
              variant="outline"
              size="md_icon"
              aria-label={t("unlink")}
              onClick={(e) => onItemUnlinked(e, props.row.original.id)}
            >
              <TbUnlink size={18} />
            </Button>
          )}
          <RoleRequire role="maintainer">
            <Button
              variant="default"
              size="md_icon"
              aria-label={t("toggle_receive_data")}
              onClick={(e) =>
                onItemDisabled(
                  e,
                  props.row.original.id,
                  props.row.original.receive_data,
                )
              }
            >
              <TbWifi size={18} />
            </Button>
          </RoleRequire>
        </div>
      ),
    },
  ];

  function selectDevice(device: any) {
    onClose();
  }

  function addAdminDevice() {
    navigate("/admin/devices/new");
    onClose();
  }

  return (
    <>
      <div className="flex flex-col">
        <div className="flex flex-row w-full h-14 items-center gap-4 px-6 justify-between">
          <Input
            className="h-8"
            placeholder={t("search")}
            onChange={handleSearch}
            value={search}
            autoComplete="off"
            width="100%"
          />

          <RoleRequire role="maintainer">
            <Button
              variant={"default"}
              size={"md"}
              onClick={() => addAdminDevice()}
            >
              {t("add")}
            </Button>
          </RoleRequire>
        </div>
      </div>

      {!data.nodes?.length ? ( // Заглушка для отсутствия данных при поиске
        <div className="flex w-full h-full justify-center items-center">
          {t("no_data")}
        </div>
      ) : (
        <MenuDataTable
          route="/admin/devices"
          data={data.nodes}
          columns={columns}
          onClickRow={selectDevice}
        />
      )}
    </>
  );
}
